import { useState, useEffect } from 'react';
import { Trophy, Medal, Award, Star, Calendar, MapPin } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

interface Achievement {
  id: string;
  title: string;
  description: string;
  date: string;
  location: string;
  type: 'medal' | 'trophy' | 'award' | 'record';
  verified: boolean;
  image?: string;
}

const mockAchievements: Achievement[] = [
  {
    id: '1',
    title: 'National Championship Gold',
    description: 'First place in the National Women\'s Basketball Championship',
    date: '2024-01-15',
    location: 'Chicago, IL',
    type: 'trophy',
    verified: true,
    image: 'https://images.pexels.com/photos/3621104/pexels-photo-3621104.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
  },
  {
    id: '2',
    title: 'Regional MVP',
    description: 'Most Valuable Player in the Western Regional Tournament',
    date: '2023-11-20',
    location: 'Los Angeles, CA',
    type: 'award',
    verified: true,
    image: 'https://images.pexels.com/photos/3621105/pexels-photo-3621105.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
  },
  {
    id: '3',
    title: 'Season Scoring Record',
    description: 'Set new season scoring record with 856 points',
    date: '2023-12-30',
    location: 'Phoenix, AZ',
    type: 'record',
    verified: true
  }
];

const Achievements = () => {
  const { user } = useAuth();
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedType, setSelectedType] = useState<string>('all');

  useEffect(() => {
    document.title = 'Achievements - FemSportNet';
    
    const fetchAchievements = async () => {
      try {
        setIsLoading(true);
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        setAchievements(mockAchievements);
      } catch (error) {
        console.error('Error fetching achievements:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAchievements();
  }, []);

  const filteredAchievements = selectedType === 'all' 
    ? achievements 
    : achievements.filter(achievement => achievement.type === selectedType);

  const getAchievementIcon = (type: string) => {
    switch (type) {
      case 'trophy':
        return <Trophy className="h-8 w-8 text-yellow-500" />;
      case 'medal':
        return <Medal className="h-8 w-8 text-yellow-500" />;
      case 'award':
        return <Award className="h-8 w-8 text-yellow-500" />;
      case 'record':
        return <Star className="h-8 w-8 text-yellow-500" />;
      default:
        return <Trophy className="h-8 w-8 text-yellow-500" />;
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen py-8 transition-colors duration-200">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Your Achievements
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Showcase your sports accomplishments and milestones
            </p>
          </div>

          {/* Achievement Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
              <Trophy className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900 dark:text-white">12</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Trophies</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
              <Medal className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900 dark:text-white">24</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Medals</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
              <Award className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900 dark:text-white">8</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Awards</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
              <Star className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900 dark:text-white">5</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Records</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex overflow-x-auto mb-8 bg-white dark:bg-gray-800 rounded-lg p-2">
            <button
              onClick={() => setSelectedType('all')}
              className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap ${
                selectedType === 'all'
                  ? 'bg-green-500 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              } transition-colors duration-200`}
            >
              All Achievements
            </button>
            <button
              onClick={() => setSelectedType('trophy')}
              className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap ${
                selectedType === 'trophy'
                  ? 'bg-green-500 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              } transition-colors duration-200`}
            >
              Trophies
            </button>
            <button
              onClick={() => setSelectedType('medal')}
              className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap ${
                selectedType === 'medal'
                  ? 'bg-green-500 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              } transition-colors duration-200`}
            >
              Medals
            </button>
            <button
              onClick={() => setSelectedType('award')}
              className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap ${
                selectedType === 'award'
                  ? 'bg-green-500 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              } transition-colors duration-200`}
            >
              Awards
            </button>
            <button
              onClick={() => setSelectedType('record')}
              className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap ${
                selectedType === 'record'
                  ? 'bg-green-500 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              } transition-colors duration-200`}
            >
              Records
            </button>
          </div>

          {/* Achievements List */}
          {isLoading ? (
            <div className="flex justify-center my-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredAchievements.map((achievement) => (
                <div
                  key={achievement.id}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden transition-all duration-200 hover:shadow-lg"
                >
                  <div className="flex flex-col md:flex-row">
                    {achievement.image && (
                      <div className="md:w-1/3">
                        <img
                          src={achievement.image}
                          alt={achievement.title}
                          className="w-full h-48 md:h-full object-cover"
                        />
                      </div>
                    )}
                    <div className={`p-6 ${achievement.image ? 'md:w-2/3' : 'w-full'}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center">
                          {getAchievementIcon(achievement.type)}
                          <div className="ml-4">
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                              {achievement.title}
                            </h3>
                            <div className="flex items-center mt-1 space-x-4">
                              <div className="flex items-center text-gray-500 dark:text-gray-400 text-sm">
                                <Calendar size={16} className="mr-1" />
                                {new Date(achievement.date).toLocaleDateString()}
                              </div>
                              <div className="flex items-center text-gray-500 dark:text-gray-400 text-sm">
                                <MapPin size={16} className="mr-1" />
                                {achievement.location}
                              </div>
                            </div>
                          </div>
                        </div>
                        {achievement.verified && (
                          <div className="bg-blue-500 rounded-full p-1">
                            <Award className="w-5 h-5 text-white" />
                          </div>
                        )}
                      </div>
                      <p className="mt-4 text-gray-600 dark:text-gray-300">
                        {achievement.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {filteredAchievements.length === 0 && (
                <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg">
                  <Trophy className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
                    No achievements found
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Start adding your achievements to showcase your success
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Add Achievement Button */}
          <div className="mt-8 text-center">
            <button className="bg-green-500 hover:bg-green-600 text-white font-medium px-6 py-3 rounded-md transition-colors duration-200">
              Add New Achievement
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Achievements;